import { type HTMLAttributes, type ReactNode } from 'react'
import { cn } from '../lib/cn'

export function Card({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('card', className)} {...props} />
}

export function CardHeader({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 pt-5 pb-3', className)} {...props} />
}

export function CardTitle({ className, ...props }: HTMLAttributes<HTMLHeadingElement>) {
  return <h3 className={cn('font-display text-base font-semibold text-ink', className)} {...props} />
}

export function CardDescription({ className, ...props }: HTMLAttributes<HTMLParagraphElement>) {
  return <p className={cn('text-xs text-slatey mt-1', className)} {...props} />
}

export function CardContent({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return <div className={cn('px-5 pb-5', className)} {...props} />
}

export function CardFooter({ className, ...props }: HTMLAttributes<HTMLDivElement>) {
  return (
    <div className={cn('px-5 py-3 border-t border-line bg-ivory/50 rounded-b-xl', className)} {...props} />
  )
}

export function StatCard({
  label,
  value,
  hint,
  icon,
  trend,
}: {
  label: string
  value: ReactNode
  hint?: string
  icon?: ReactNode
  trend?: { direction: 'up' | 'down' | 'flat'; value: string }
}) {
  return (
    <Card className="p-5">
      <div className="flex items-start justify-between gap-3">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-slatey">{label}</p>
          <p className="mt-2 text-2xl font-display font-semibold text-ink leading-none">{value}</p>
          {hint ? <p className="mt-2 text-xs text-slatey">{hint}</p> : null}
        </div>
        {icon ? (
          <div className="size-10 rounded-md bg-ivory flex items-center justify-center text-forest">{icon}</div>
        ) : null}
      </div>
      {trend ? (
        <p
          className={cn(
            'mt-3 text-xs font-medium',
            trend.direction === 'up' && 'text-forest',
            trend.direction === 'down' && 'text-danger',
            trend.direction === 'flat' && 'text-slatey',
          )}
        >
          {trend.direction === 'up' ? '↑ ' : trend.direction === 'down' ? '↓ ' : '→ '}
          {trend.value}
        </p>
      ) : null}
    </Card>
  )
}
